// ─── Theme ───────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'theme';
const DARK  = 'dark';
const LIGHT = 'light';

const getPreferred = () => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return stored;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? DARK : LIGHT;
};

const applyTheme = (theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem(STORAGE_KEY, theme);
};

// ─── Scroll reveal ───────────────────────────────────────────────────────────

const initReveal = () => {
    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        },
        { threshold: 0.12 }
    );

    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
};

// ─── Mobile nav ──────────────────────────────────────────────────────────────

const initMobileNav = () => {
    const toggle = document.getElementById('navToggle');
    const nav    = document.getElementById('main-nav');

    if (!toggle || !nav) return;

    toggle.addEventListener('click', () => {
        const isOpen = nav.classList.toggle('is-open');
        toggle.classList.toggle('is-open', isOpen);
        toggle.setAttribute('aria-expanded', String(isOpen));
    });

    // Close nav on link click
    nav.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
            nav.classList.remove('is-open');
            toggle.classList.remove('is-open');
            toggle.setAttribute('aria-expanded', 'false');
        });
    });
};

// ─── Header scroll shadow ────────────────────────────────────────────────────

const initHeaderScroll = () => {
    const header = document.getElementById('site-header');
    if (!header) return;

    window.addEventListener('scroll', () => {
        header.classList.toggle('is-scrolled', window.scrollY > 10);
    }, { passive: true });
};

// ─── Modal ────────────────────────────────────────────────────────────────────

const initModal = () => {
    const backdrop    = document.getElementById('modalBackdrop');
    const closeBtn    = document.getElementById('modalClose');
    const illustration = document.getElementById('modalIllustration');
    const modalBody   = document.getElementById('modalBody');

    if (!backdrop) return;

    const open = (ilSvg, bodyHtml) => {
        illustration.innerHTML = ilSvg;
        modalBody.innerHTML    = bodyHtml;
        backdrop.classList.add('is-open');
        backdrop.removeAttribute('aria-hidden');
        document.body.style.overflow = 'hidden';
        closeBtn.focus();
    };

    const close = () => {
        backdrop.classList.remove('is-open');
        backdrop.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    };

    // Plant cards
    document.querySelectorAll('.plant-card').forEach((card) => {
        card.addEventListener('click', () => {
            const svgEl   = card.querySelector('.plant-illustration')?.innerHTML || '';
            const latin   = card.querySelector('.plant-latin')?.textContent || '';
            const title   = card.querySelector('h3')?.textContent || '';
            const desc    = card.querySelector('p')?.textContent || '';
            const season  = card.querySelector('.plant-season')?.textContent || '';

            let body = '';
            if (latin)  body += `<span class="modal-latin">${latin}</span>`;
            body += `<h2 class="modal-title">${title}</h2>`;
            if (desc)   body += `<p class="modal-description">${desc}</p>`;
            if (season) body += `<span class="modal-season">🌿 ${season}</span>`;

            open(svgEl, body);
        });
    });

    // Recipe cards
    document.querySelectorAll('.recipe-card').forEach((card) => {
        card.addEventListener('click', (e) => {
            // Don't intercept clicks on the "Voir la recette" link
            if (e.target.closest('.recipe-link')) return;

            const svgEl    = card.querySelector('.recipe-visual')?.innerHTML || '';
            const tag      = card.querySelector('.recipe-tag')?.textContent || '';
            const title    = card.querySelector('h3')?.textContent || '';
            const desc     = card.querySelector('p')?.textContent || '';
            const link     = card.querySelector('.recipe-link');
            const portions = card.dataset.portions || '';
            const prep     = card.dataset.prep || '';
            const cuisson  = card.dataset.cuisson || '';
            const ingRaw   = card.dataset.ingredients || '';
            const etapesRaw = card.dataset.etapes || '';

            let body = '';
            if (tag) body += `<span class="modal-tag">${tag}</span>`;
            body += `<h2 class="modal-title">${title}</h2>`;
            if (desc) body += `<p class="modal-description">${desc}</p>`;

            // Meta row (portions / timing)
            const metaItems = [];
            if (portions && portions !== '0') metaItems.push(`<span class="modal-meta-item">🍽 ${portions} portion${portions > 1 ? 's' : ''}</span>`);
            if (prep)    metaItems.push(`<span class="modal-meta-item">🕐 Prépa : ${prep}</span>`);
            if (cuisson) metaItems.push(`<span class="modal-meta-item">🔥 Cuisson : ${cuisson}</span>`);
            if (metaItems.length) body += `<div class="modal-meta">${metaItems.join('')}</div>`;

            // Ingredients
            if (ingRaw.trim()) {
                const lines = ingRaw.split('\n').map(l => l.trim()).filter(Boolean);
                body += `<div class="modal-section"><h4 class="modal-section-title">Ingrédients</h4><ul class="modal-ingredients">${lines.map(l => `<li>${l}</li>`).join('')}</ul></div>`;
            }

            // Steps
            if (etapesRaw.trim()) {
                const steps = etapesRaw.split('\n').map(l => l.trim()).filter(Boolean);
                body += `<div class="modal-section"><h4 class="modal-section-title">Préparation</h4><ol class="modal-steps">${steps.map(s => `<li>${s}</li>`).join('')}</ol></div>`;
            }

            if (link && link.href && !link.href.endsWith('#')) {
                body += `<a href="${link.href}" class="modal-link">Voir la recette complète →</a>`;
            }

            open(svgEl, body);
        });
    });

    // Close triggers
    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
};

// ─── Inscription modal ────────────────────────────────────────────────────────

const initInscriptionModal = () => {
    const backdrop  = document.getElementById('inscModalBackdrop');
    const closeBtn  = document.getElementById('inscModalClose');
    const form      = document.getElementById('inscForm');
    const titleEl   = document.getElementById('inscModalTitle');
    const metaEl    = document.getElementById('inscModalMeta');
    const atelierIdInput = document.getElementById('inscAtelierId');
    const globalErr = document.getElementById('inscGlobalError');
    const successEl = document.getElementById('inscSuccess');
    const successMsg = document.getElementById('inscSuccessMsg');
    const submitBtn = document.getElementById('inscSubmit');
    const submitLabel  = submitBtn?.querySelector('.insc-submit-label');
    const submitSpinner = submitBtn?.querySelector('.insc-submit-spinner');

    if (!backdrop) return;

    const openModal = (btn) => {
        const titre = btn.dataset.atelierTitre || '';
        const date  = btn.dataset.atelierDate  || '';
        const lieu  = btn.dataset.atelierLieu  || '';

        titleEl.textContent     = titre;
        atelierIdInput.value    = btn.dataset.atelierId || '';

        const metaParts = [date, lieu].filter(Boolean);
        metaEl.textContent  = metaParts.join(' · ');
        metaEl.hidden       = metaParts.length === 0;

        // Reset form
        form.reset();
        form.hidden     = false;
        successEl.hidden = true;
        globalErr.hidden = true;
        document.querySelectorAll('.insc-field-error').forEach(el => { el.textContent = ''; });

        backdrop.classList.add('is-open');
        backdrop.removeAttribute('aria-hidden');
        document.body.style.overflow = 'hidden';
        closeBtn.focus();
    };

    const closeModal = () => {
        backdrop.classList.remove('is-open');
        backdrop.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
    };

    // Open on inscription buttons
    document.querySelectorAll('.js-open-insc').forEach((btn) => {
        btn.addEventListener('click', () => openModal(btn));
    });

    closeBtn.addEventListener('click', closeModal);
    backdrop.addEventListener('click', (e) => { if (e.target === backdrop) closeModal(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && backdrop.classList.contains('is-open')) closeModal(); });

    // Form submit
    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        // Client-side validation
        let valid = true;
        const nom   = form.nom.value.trim();
        const email = form.email.value.trim();

        document.getElementById('errNom').textContent    = '';
        document.getElementById('errEmail').textContent  = '';
        document.getElementById('errConsent').textContent = '';
        globalErr.hidden = true;

        if (!nom) {
            document.getElementById('errNom').textContent = 'Le nom est requis.';
            valid = false;
        }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            document.getElementById('errEmail').textContent = 'Adresse e-mail invalide.';
            valid = false;
        }
        if (!form.consent.checked) {
            document.getElementById('errConsent').textContent = 'Veuillez accepter la politique de confidentialité.';
            valid = false;
        }
        if (!valid) return;

        // Submit
        submitBtn.disabled = true;
        submitLabel.hidden  = true;
        submitSpinner.hidden = false;

        try {
            const res  = await fetch('/inscription.php', {
                method: 'POST',
                body:   new FormData(form),
            });
            const data = await res.json();

            if (data.ok) {
                form.hidden      = true;
                successEl.hidden = false;
                successMsg.textContent = data.message || 'Votre demande a bien été enregistrée.';
            } else {
                globalErr.textContent = data.error || 'Une erreur est survenue. Veuillez réessayer.';
                globalErr.hidden = false;
            }
        } catch {
            globalErr.textContent = 'Erreur de connexion. Veuillez réessayer.';
            globalErr.hidden = false;
        } finally {
            submitBtn.disabled  = false;
            submitLabel.hidden  = false;
            submitSpinner.hidden = true;
        }
    });
};

// ─── Init ─────────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
    applyTheme(getPreferred());

    document.getElementById('themeToggle').addEventListener('click', () => {
        const current = document.documentElement.getAttribute('data-theme');
        applyTheme(current === DARK ? LIGHT : DARK);
    });

    initReveal();
    initMobileNav();
    initHeaderScroll();
    initModal();
    initInscriptionModal();
});
